import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Mail, ArrowUp } from "lucide-react";

/**
 * FloatingDock — a vertical rail of quick contact actions that fades in
 * after the user scrolls past the hero. Includes a back-to-top button.
 */
const FloatingDock = ({ onOpenContact }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setVisible(window.scrollY > window.innerHeight * 0.6);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toTop = () =>
    window.scrollTo({ top: 0, behavior: "smooth" });

  const items = [
    {
      key: "phone",
      title: "Call",
      icon: Phone,
      accent: "var(--accent-blue)",
      onClick: () => (window.location.href = "tel:+254725979245"),
      testid: "dock-phone-btn",
    },
    {
      key: "email",
      title: "Email",
      icon: Mail,
      accent: "var(--accent-crimson-hover)",
      onClick: () => (window.location.href = "mailto:contact@tafraenergy.com"),
      testid: "dock-email-btn",
    },
    {
      key: "top",
      title: "Back to top",
      icon: ArrowUp,
      accent: "var(--text)",
      onClick: toTop,
      testid: "dock-top-btn",
    },
  ];

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, x: 14 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 14 }}
          transition={{ duration: 0.4, ease: [0.2, 0.7, 0.2, 1] }}
          className="hidden md:flex fixed right-4 sm:right-6 bottom-24 z-40 flex-col gap-2"
          data-testid="floating-dock"
        >
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <motion.button
                key={it.key}
                onClick={it.onClick}
                data-testid={it.testid}
                title={it.title}
                aria-label={it.title}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.06 }}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.96 }}
                className="group relative w-11 h-11 flex items-center justify-center btn-sharp backdrop-blur-xl"
                style={{
                  background: "var(--surface-glass)",
                  border: "1px solid var(--border)",
                  color: it.accent,
                  boxShadow: "0 4px 24px rgba(0,0,0,0.20)",
                }}
              >
                <span
                  aria-hidden
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    background: `radial-gradient(circle at 50% 50%, ${it.accent}22, transparent 70%)`,
                  }}
                />
                <Icon className="relative w-4 h-4" strokeWidth={1.6} />
                <span
                  aria-hidden
                  className="pointer-events-none absolute right-full mr-3 px-2 py-1 font-mono-spec text-[10px] tracking-[0.18em] uppercase whitespace-nowrap btn-sharp opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    background: "var(--surface-elev)",
                    color: "var(--text)",
                    border: "1px solid var(--border)",
                  }}
                >
                  {it.title}
                </span>
              </motion.button>
            );
          })}

          {onOpenContact && (
            <motion.button
              onClick={onOpenContact}
              data-testid="dock-message-btn"
              title="Send a message"
              aria-label="Send a message"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.96 }}
              className="group relative w-11 h-11 flex items-center justify-center btn-sharp"
              style={{
                background: "var(--accent-blue)",
                color: "var(--bg)",
                boxShadow: "0 0 22px var(--accent-blue-glow)",
                border: "1px solid transparent",
              }}
            >
              <Mail className="w-4 h-4" strokeWidth={1.8} />
              <span
                aria-hidden
                className="pointer-events-none absolute right-full mr-3 px-2 py-1 font-mono-spec text-[10px] tracking-[0.18em] uppercase whitespace-nowrap btn-sharp opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background: "var(--surface-elev)",
                  color: "var(--text)",
                  border: "1px solid var(--border)",
                }}
              >
                Send a message
              </span>
            </motion.button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FloatingDock;
